import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from '@reservamos/elements';
import Checkbox from 'ui/atoms/Checkbox';
import useLineTerms from 'hooks/purchase/useLineTerms';

/**
 * LineSpecificTerms component.
 * Render additional terms and conditions checkboxes for specific bus lines.
 * @param {object} props - acceptedTerms, onToggle, hasError.
 * @param {object} props.acceptedTerms - Acceptance map keyed by lineId.
 * @param {function} props.onToggle - Toggle handler receiving a lineId.
 * @param {boolean} props.hasError - Whether to show error state on unchecked checkboxes.
 */
const LineSpecificTerms = ({ acceptedTerms, onToggle, hasError }) => {
  const { t } = useTranslation('passengers');
  const { terms } = useLineTerms();

  if (!terms.length) return null;

  return terms.map(({ lineId, agreementsUrl, termsUrl }) => {
    const isChecked = Boolean(acceptedTerms?.[lineId]);
    const showError = hasError && !isChecked;

    return (
      <div key={lineId} className="flex items-start gap-2 mt-1">
        <Checkbox
          id={`line-terms-checkbox-${lineId}`}
          onChange={() => onToggle(lineId)}
          checked={isChecked}
          hasError={showError}
        />
        <label
          htmlFor={`line-terms-checkbox-${lineId}`}
          className="text-sm leading-tight cursor-pointer gray-medium"
        >
          <Trans
            i18nKey="passengers:lineTerms.label"
            components={[
              <Link
                text={t('lineTerms.links.agreements', {
                  defaultValue: 'Convenios de colaboración',
                })}
                href={agreementsUrl}
                size="S"
                type="accent"
                weight="regular"
                newTab
              />,
              <Link
                text={t('lineTerms.links.terms', { defaultValue: 'Términos y Condiciones' })}
                href={termsUrl}
                size="S"
                type="accent"
                weight="regular"
                newTab
              />,
            ]}
          />
        </label>
      </div>
    );
  });
};

LineSpecificTerms.propTypes = {
  acceptedTerms: PropTypes.objectOf(PropTypes.bool).isRequired,
  onToggle: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
};

LineSpecificTerms.defaultProps = {
  hasError: false,
};

export default LineSpecificTerms;
